import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import moment from 'moment';
import { stringify } from 'query-string';

import {
  CONNECTED_APP_ACTIVE,
  CONNECTED_APP_EXPIRED
} from 'constants/connectedAppsStatuses';
import { getIsSandBox } from '../utils/common';
import Auth from '../service/Auth';
import { getDateRanges } from 'service/Utils';
import { getPermittedScopes } from '../service/AccessMerchant';

import { getRootEndpoint, getPortalUrl } from '../service/Utils/endpoints';
import { BATCH_STATUS_OPEN, CLIENT_TYPE, MAIN_API, PROCESSOR_CHC, PROCESSORS } from '../constants';

const rawBaseQuery = (url) =>
  fetchBaseQuery({
    baseUrl: url,
    prepareHeaders: (headers) => {
      const token = Auth.getToken();

      headers.set('Content-Type', 'application/json');
      headers.set('Client-Type', CLIENT_TYPE);
      headers.set('API-Version', 'v2020');

      if (!token) {
        return headers;
      }

      headers.set('authorization', `Bearer ${token}`);
      return headers;
    },
    paramsSerializer: (params) => stringify(params, { arrayFormat: 'index' })
  });

const dynamicBaseQuery = async (args, api, extraOptions) => {
  const global = api.getState().global;
  const editor = api.getState().portalApp;

  const root = args;
  const isSandBox = getIsSandBox({ editor, global });
  const rootUrl = getRootEndpoint(root, { isSandBox });

  return rawBaseQuery(rootUrl)(args, api, extraOptions);
};

const createTdsAccessToken = async (apiKey, api) => {
  const global = api.getState().global;
  const editor = api.getState().portalApp;
  const isSandBox = getIsSandBox({ editor, global });
  const rootUrl = getRootEndpoint(MAIN_API, { isSandBox });

  const response = await fetch(`${rootUrl}/access-tokens`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Client-Type': CLIENT_TYPE,
      'API-Version': 'v2020',
      Authorization: `Bearer ${apiKey}`
    },
    body: JSON.stringify({ scope: '3ds_config', type: 'limited_scope' })
  });

  const data = await response.json();
  if (!response.ok) {
    throw data;
  }

  return data.accessToken;
};

export const officeApi = createApi({
  reducerPath: 'officeApi',
  tagTypes: [
    'invoices',
    'notifications',
    'dashboard',
    'invoicingStats',
    'cardTypeByMonth',
    'customerLoyalty',
    'spendStats',
    'receipts',
    'paymentHistory',
    'achReport',
    'batchReport',
    'chargeBacks',
    'chargeBack',
    'chargeBackNotes',
    'apiKeys',
    'statements',
    'retrievals',
    'profile',
    'suggestions',
    'timeZones',
    'users',
    'blockedAccounts',
    'merchantAccessStatus',
    'merchantAccessScopes',
    'subMerchants',
    'applePayIOSApps',
    'oAuthSlackUser',
    'oAuthQuickbooksUser',
    'invoicingSettings',
    'merchantUsers',
    'customer',
    'customers',
    'capital',
    'merchant'
  ],
  baseQuery: dynamicBaseQuery,
  endpoints: (builder) => ({
    getNotifications: builder.query({
      query: (merchantId) => `merchants/${merchantId}/notifications`,
      providesTags: (result, error, arg) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'notifications', id })),
              'notifications'
            ]
          : ['notifications']
    }),
    markNotification: builder.mutation({
      query: ({ merchantId, userId, id }) => ({
        url: `merchants/${merchantId}/notifications/${id}`,
        method: 'PUT',
        body: { $addToSet: { viewedBy: userId } }
      }),
      invalidatesTags: ['notifications']
    }),
    markAllNotifications: builder.mutation({
      query: ({ merchantId, userId }) => ({
        url: `merchants/${merchantId}/notifications`,
        method: 'PATCH',
        body: { $addToSet: { viewedBy: userId } }
      }),
      invalidatesTags: ['notifications']
    }),
    getDashboard: builder.query({
      query: ({ merchantId, ...arg }) => {
        const { monthToDate, last30Days } = getDateRanges();
        const { data } = arg;

        const now = moment();
        const dateTo = now.format('MM/DD/YYYY');
        const dateFrom = moment(data).format('MM/DD/YYYY');
        const newQ = { dateFrom, dateTo };

        return {
          url: `merchants/${merchantId}/dashboard`,
          params: {
            dateRange: [newQ.dateFrom, newQ.dateTo],
            customerLoyaltyDateRange: [monthToDate.dateFrom, monthToDate.dateTo],
            spendStatsDateRange: [monthToDate.dateFrom, monthToDate.dateTo],
            invoicingStatsDateRange: [last30Days.dateFrom, last30Days.dateTo]
          }
        };
      },
      providesTags: (result, error, { merchantId, data }) => [
        { type: 'dashboard', id: merchantId, data }
      ]
    }),
    getInvoicingStats: builder.query({
      query: ({ merchantId, ...arg }) => {
        const { dateFrom, dateTo } = arg;

        return {
          url: `merchants/${merchantId}/dashboard`,
          params: {
            invoicingStatsDateRange: [dateFrom, dateTo],
            pick: ['getInvoicingStats']
          }
        };
      },
      providesTags: (result, error, { merchantId, dateFrom, dateTo }) => [
        { type: 'invoicingStats', merchantId, dateFrom, dateTo }
      ]
    }),
    getCardTypeByMonth: builder.query({
      query: ({ merchantId, ...arg }) => {
        const { dateFrom, dateTo } = arg;

        return {
          url: `merchants/${merchantId}/dashboard`,
          params: {
            dateRange: [dateFrom, dateTo],
            pick: ['getByCardBrand']
          }
        };
      },
      providesTags: (result, error, { merchantId, dateFrom, dateTo }) => [
        { type: 'cardTypeByMonth', merchantId, dateFrom, dateTo }
      ]
    }),
    getCustomerLoyalty: builder.query({
      query: ({ merchantId, ...arg }) => {
        const { dateFrom, dateTo } = arg;

        return {
          url: `merchants/${merchantId}/dashboard`,
          params: {
            customerLoyaltyDateRange: [dateFrom, dateTo],
            pick: ['getCustomerLoyalty']
          }
        };
      },
      providesTags: (result, error, { merchantId, dateFrom, dateTo }) => [
        { type: 'customerLoyalty', merchantId, dateFrom, dateTo }
      ]
    }),
    getSpendStats: builder.query({
      query: ({ merchantId, ...arg }) => {
        const { dateFrom, dateTo } = arg;

        return {
          url: `merchants/${merchantId}/dashboard`,
          params: {
            spendStatsDateRange: [dateFrom, dateTo],
            pick: ['getSpendStats']
          }
        };
      },
      providesTags: (result, error, { merchantId, dateFrom, dateTo }) => [
        { type: 'spendStats', merchantId, dateFrom, dateTo }
      ]
    }),
    getReceipts: builder.query({
      query: ({ merchantId, ...arg }) => {
        return {
          url: `merchants/${merchantId}/dashboard`,
          params: {
            merchantId,
            ...arg
          }
        };
      },
      providesTags: (result, error, { merchantId, dateFrom, dateTo }) => [
        { type: 'receipts', merchantId, dateFrom, dateTo }
      ]
    }),
    getPaymentHistory: builder.query({
      query: ({ merchantId, ...args }) => {
        return {
          url: `merchants/${merchantId}/payment-history`,
          params: {
            ...args
          }
        };
      },
      providesTags: (result, error, { merchantId, ...args }) => [
        { type: 'paymentHistory', merchantId, args }
      ]
    }),
    getAchReport: builder.query({
      query: ({ merchantId, ...arg }) => {
        return {
          url: `merchants/${merchantId}/transactions`,
          params: {
            ...arg,
            processor: PROCESSOR_CHC
          }
        };
      },
      providesTags: (result, error, { merchantId, ...args }) => [
        { type: 'achReport', merchantId, ...args }
      ]
    }),
    getBatchReport: builder.query({
      query: ({ merchantId, processor, date, ...rest }) => {
        const processorsForOpenBatches = [PROCESSORS.PSP, PROCESSORS.NTK, PROCESSORS.FDC];

        if (processorsForOpenBatches.includes(processor)) {
          return {
            url: `merchants/${merchantId}/batches`,
            params: {
              batchStatus: BATCH_STATUS_OPEN
            }
          };
        }

        let newQuery = { date, ...rest };
        if (date) {
          const dateArr = (date || '').split('-');
          const [dateFrom, dateTo] = [
            dateArr.slice(0, 3).join('-'),
            dateArr.slice(3).join('-')
          ];
          newQuery = { ...rest, dateFrom, dateTo };
        }

        return {
          url: `merchants/${merchantId}/batches`,
          params: {
            ...newQuery
          }
        };
      },
      providesTags: (result, error, { merchantId, processor, ...query }) => [
        { type: 'batchReport', merchantId, processor, ...query }
      ]
    }),
    getChargeBacks: builder.query({
      query: ({ merchantId, ...arg }) => {
        const date = arg && arg.query;
        let newQuery = { ...arg };

        if (date) {
          const now = moment();
          const dateTo = now.format('MM/DD/YYYY');
          const dateFrom = date;
          newQuery = { ...arg, dateFrom, dateTo };
        }

        return {
          url: `merchants/${merchantId}/chargebacks`,
          params: {
            ...newQuery
          }
        };
      },
      providesTags: (result, error, arg) => [
        {
          type: 'chargeBacks',
          id: arg.merchantId,
          dateFrom: arg.dateFrom,
          dateTo: arg.dateTo
        }
      ]
    }),
    getChargeBack: builder.query({
      query: ({ merchantId, id }) => {
        return {
          url: `merchants/${merchantId}/chargebacks/${id}`
        };
      },
      providesTags: (result, error, arg) => [
        { type: 'chargeBack', merchantId: arg.merchantId, id: arg.id }
      ]
    }),
    getNotes: builder.query({
      query: ({ merchantId, id }) => {
        return {
          url: `merchants/${merchantId}/notifications`,
          params: {
            detailType: 'Chargebacks',
            detailId: id
          }
        };
      },
      providesTags: (result, error, arg) => [
        { type: 'chargeBackNotes', merchantId: arg.merchantId, id: arg.id }
      ]
    }),
    getApiKeys: builder.query({
      query: ({ merchantId, ...query }) => {
        return {
          url: `merchants/${merchantId}/manage-apikeys`,
          params: {
            ...query
          }
        };
      },
      providesTags: (result, error, { merchantId, ...query }) => [
        { type: 'apiKeys', merchantId, ...query }
      ]
    }),
    updateApiKeys: builder.mutation({
      query: ({ merchantId, ...body }) => ({
        url: `merchants/${merchantId}/manage-apikeys`,
        method: 'POST',
        body
      }),
      invalidatesTags: [{ type: 'apiKeys' }]
    }),
    getStatementsReport: builder.query({
      query: ({ merchantId, ...query }) => {
        return {
          url: `merchants/${merchantId}/statements`,
          params: {
            ...query
          }
        };
      },
      providesTags: (result, error, { merchantId, ...query }) => [
        { type: 'statements', merchantId, ...query }
      ]
    }),
    getRetrievals: builder.query({
      query: ({ merchantId, ...query }) => {
        return {
          url: `merchants/${merchantId}/retrievals`,
          params: {
            ...query
          }
        };
      },
      providesTags: (result, error, { merchantId, ...query }) => [
        { type: 'retrievals', merchantId, ...query }
      ]
    }),
    getProfile: builder.query({
      query: (query) => {
        return {
          url: 'profile',
          params: query
            ? {
                ...query
              }
            : undefined
        };
      },

      providesTags: (_, __, query) => [{ type: 'profile', ...query }]
    }),
    getSuggestions: builder.query({
      query: ({ merchantId, userId }) => {
        return {
          url: `merchants/${merchantId}/suggestions`,
          params: {
            merchantId,
            userId
          }
        };
      },

      providesTags: (_, __, { merchantId, userId }) => [
        { type: 'suggestions', merchantId, userId }
      ]
    }),
    getTimeZones: builder.query({
      query: () => {
        return {
          url: 'profile-time-zones'
        };
      },

      providesTags: () => [{ type: 'timeZones' }]
    }),
    getUsers: builder.query({
      query: ({ merchantId, ...query }) => {
        return {
          url: `merchants/${merchantId}/users`,
          params: {
            ...query
          }
        };
      },

      providesTags: (_, __, { merchantId, ...query }) => [
        { type: 'users', merchantId, ...query }
      ]
    }),
    getBlockedAccounts: builder.query({
      query: ({ merchantId, ...query }) => {
        return {
          url: `merchants/${merchantId}/blocked-accounts`,
          params: {
            ...query
          }
        };
      },

      providesTags: (_, __, { merchantId, ...query }) => [
        { type: 'blockedAccounts', merchantId, ...query }
      ]
    }),
    getMerchantAccessStatus: builder.query({
      query: ({ merchantId, ...query }) => {
        return {
          url: `profile-merchant-access-status/${merchantId}`,
          params: {
            ...query
          }
        };
      },

      providesTags: (_, __, { merchantId, ...query }) => [
        { type: 'merchantAccessStatus', merchantId, ...query }
      ]
    }),
    getMerchantAccessScopes: builder.query({
      query: ({ merchantId }) => {
        return {
          url: `profile-merchant-access-scopes/${merchantId}`
        };
      },

      providesTags: (_, __, { merchantId }) => [
        { type: 'merchantAccessScopes', merchantId }
      ]
    }),
    getMerchantUsers: builder.query({
      query: ({ merchantId, ...query }) => {
        return {
          url: `merchants/${merchantId}/users`,
          params: {
            ...query
          }
        };
      },

      providesTags: (_, __, { merchantId }) => [
        { type: 'merchantUsers', merchantId }
      ]
    }),
    getSubMerchants: builder.query({
      query: ({ merchantId, ...query }) => {
        return {
          url: `merchants/${merchantId}/submerchants`,
          params: {
            ...query
          }
        };
      },
      providesTags: (_, __, { merchantId, ...query }) => [
        { type: 'subMerchants', merchantId, ...query }
      ]
    }),
    getOAuthSlackUser: builder.query({
      query: ({ merchantId, ...query }) => {
        return {
          url: `merchants/${merchantId}/oauth-slack-user`,
          params: {
            ...query
          }
        };
      },

      providesTags: (_, __, { merchantId, ...query }) => [
        { type: 'oAuthSlackUser', merchantId, ...query }
      ]
    }),
    getOAuthQuickbooksUser: builder.query({
      query: ({ merchantId, ...query }) => {
        return {
          url: `merchants/${merchantId}/oauth-quickbooks-user`,
          params: {
            ...query
          }
        };
      },

      providesTags: (_, __, { merchantId, ...query }) => [
        { type: 'oAuthQuickbooksUser', merchantId, ...query }
      ]
    }),
    getInvoicingSettings: builder.query({
      query: ({ merchantId, ...query }) => {
        return {
          url: `merchants/${merchantId}/invoicing-settings`,
          params: {
            ...query
          }
        };
      },

      providesTags: (_, __, { merchantId, ...query }) => [
        { type: 'invoicingSettings', merchantId, ...query }
      ]
    }),
    getSettings: builder.query({
      queryFn: async ({ merchantId, userId, ...query }, selfApi) => {
        const fullQuery = { merchantId, userId, ...query };
        const oauthUserQuery = {
          'status[$in]': [CONNECTED_APP_ACTIVE, CONNECTED_APP_EXPIRED],
          '$sort[createdAt]': -1,
          merchant: merchantId
        };
        const { custom, ...otherQuery } = query;
        const queryData = custom === '' ? { ...otherQuery } : { ...query };

        try {
          let requests = {
            suggestions: getPermittedScopes(
              {
                accessRules: {
                  suggestions: ['manage']
                }
              },
              merchantId
            )
              ? await selfApi.getSuggestions({ merchantId, userId })
              : undefined,
            timeZones: await selfApi.getTimeZones(),
            users: getPermittedScopes(
              {
                accessRules: {
                  users: ['find', 'get']
                }
              },
              merchantId
            )
              ? await selfApi.getUsers({ merchantId, userId })
              : undefined,
            blockedAccounts: getPermittedScopes(
              {
                accessRules: {
                  'blocked-accounts': ['manage']
                }
              },
              merchantId
            )
              ? await selfApi.getBlockedAccounts({ merchantId })
              : undefined
          };

          if (merchantId && userId) {
            requests = {
              ...requests,
              profile: await selfApi.getProfile(),
              permissionScopes: await selfApi.getMerchantAccessScopes({ merchantId }),
              permissionStatus: await selfApi.getMerchantAccessStatus({ merchantId }),
              merchantUsers: getPermittedScopes(
                {
                  accessRules: {
                    users: ['find', 'get']
                  }
                },
                merchantId
              )
                ? await selfApi.getMerchantUsers({ merchantId, ...queryData })
                : undefined,
              receipts: getPermittedScopes(
                {
                  accessRules: {
                    receipts: ['find', 'get']
                  }
                },
                merchantId
              )
                ? await selfApi.getReceipts({ merchantId })
                : undefined,
              slackUsers: getPermittedScopes(
                {
                  accessRules: {
                    'slack-integration': ['find', 'get']
                  }
                },
                merchantId
              )
                ? await selfApi.getOAuthSlackUser({ merchantId, ...oauthUserQuery })
                : undefined,
              quickbooksUsers: getPermittedScopes(
                {
                  accessRules: {
                    'quickbooks-integration': ['find', 'get']
                  }
                },
                merchantId
              )
                ? await selfApi.getOAuthSlackUser({ merchantId, ...oauthUserQuery })
                : undefined,
              invoicingSettings: getPermittedScopes(
                {
                  accessRules: {
                    'invoicing-settings': ['manage']
                  }
                },
                merchantId
              )
                ? await selfApi.getInvoicingSettings({ merchantId, ...query })
                : undefined,
              paymentTerminals: undefined
            };
          }
          return { data: requests };
        } catch (error) {
          return { error };
        }
      }
    }),
    getCustomers: builder.query({
      query: ({ merchantId, ...query }) => {
        return {
          url: `merchants/${merchantId}/customers`,
          params: {
            ...query
          }
        };
      },
      selectFromResult: (res) => res,
      providesTags: (_, __, { merchantId, ...query }) => [
        { type: 'customers', merchantId, ...query }
      ]
    }),
    getCustomer: builder.query({
      query: ({ merchantId, customerId, ...query }) => {
        return {
          url: `merchants/${merchantId}/customers/${customerId}`,
          params: {
            ...query
          }
        };
      },

      providesTags: (_, __, { merchantId, customerId }) => [
        { type: 'customer', merchantId, customerId }
      ]
    }),
    getInvoices: builder.query({
      query: ({ merchantId, ...query }) => {
        return {
          url: `merchants/${merchantId}/invoices`,
          params: {
            ...query
          }
        };
      },

      providesTags: (_, __, { merchantId, ...query }) => [
        { type: 'invoices', merchantId, ...query }
      ]
    }),
    getCapital: builder.query({
      query: ({ merchantId, ...query }) => {
        return {
          url: `merchants/${merchantId}/capital`,
          params: {
            ...query
          }
        };
      },

      providesTags: (_, __, { merchantId, ...query }) => [
        { type: 'capital', merchantId, ...query }
      ]
    }),
    createAccessToken: builder.mutation({
      queryFn: async ({ apiKey, scope = 'web-sdk', type = 'limited_scope' }, api) => {
        const global = api.getState().global;
        const editor = api.getState().portalApp;
        const isSandBox = getIsSandBox({ editor, global });
        const rootUrl = getRootEndpoint(MAIN_API, { isSandBox });

        try {
          const response = await fetch(`${rootUrl}/access-tokens`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Client-Type': CLIENT_TYPE,
              'API-Version': 'v2020',
              Authorization: `Bearer ${apiKey}`
            },
            body: JSON.stringify({ scope, type })
          });

          const data = await response.json();

          if (!response.ok) {
            return { error: data };
          }

          return { data };
        } catch (error) {
          return { error: { message: error.message } };
        }
      }
    }),
    getTdsMerchant: builder.query({
      queryFn: async ({ merchantId, apiKey }, api) => {
        const portalUrls = getPortalUrl();

        try {
          const accessToken = await createTdsAccessToken(apiKey, api);

          const response = await fetch(
            `${portalUrls.threeDSApiUrl}/api/merchants/${merchantId}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`
              }
            }
          );

          if (response.status === 404) {
            return { data: null };
          }

          const data = await response.json();

          if (!response.ok) {
            return { error: { message: data.error || JSON.stringify(data) } };
          }

          return { data };
        } catch (error) {
          return { error: { message: error.message } };
        }
      }
    }),
    createTdsMerchant: builder.mutation({
      queryFn: async ({ apiKey, payload }, api) => {
        const portalUrls = getPortalUrl();

        try {
          const accessToken = await createTdsAccessToken(apiKey, api);

          const response = await fetch(
            `${portalUrls.threeDSApiUrl}/api/merchants`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`
              },
              body: JSON.stringify(payload)
            }
          );

          const data = await response.json();

          if (!response.ok) {
            return { error: { message: data.error || JSON.stringify(data) } };
          }

          return { data };
        } catch (error) {
          return { error: { message: error.message } };
        }
      },
      async onQueryStarted({ merchantId, apiKey }, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        dispatch(
          officeApi.util.updateQueryData('getTdsMerchant', { merchantId, apiKey }, () => data)
        );
      }
    }),
    updateTdsMerchant: builder.mutation({
      queryFn: async ({ merchantId, apiKey, payload }, api) => {
        const portalUrls = getPortalUrl();

        try {
          const accessToken = await createTdsAccessToken(apiKey, api);

          const response = await fetch(
            `${portalUrls.threeDSApiUrl}/api/merchants/${merchantId}`,
            {
              method: 'PATCH',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`
              },
              body: JSON.stringify(payload)
            }
          );

          const data = await response.json();

          if (!response.ok) {
            return { error: { message: data.error || JSON.stringify(data) } };
          }

          return { data };
        } catch (error) {
          return { error: { message: error.message } };
        }
      },
      async onQueryStarted({ merchantId, apiKey }, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        dispatch(
          officeApi.util.updateQueryData('getTdsMerchant', { merchantId, apiKey }, () => data)
        );
      }
    }),
    authGhostMerchant: builder.mutation({
      queryFn: async ({ id }, api) => {
        const global = api.getState().global;
        const editor = api.getState().portalApp;
        const isSandBox = getIsSandBox({ editor, global });
        const rootUrl = getRootEndpoint(MAIN_API, { isSandBox });

        try {
          const response = await fetch(`${rootUrl}/office-auth/ghost/merchant`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Client-Type': CLIENT_TYPE,
              'API-Version': 'v2020',
              Authorization: `Bearer ${Auth.getToken()}`
            },
            body: JSON.stringify({ id })
          });
          const data = await response.json();
          if (!response.ok) return { error: data };
          return { data };
        } catch (error) {
          return { error: { message: error.message } };
        }
      }
    }),
    getMerchant: builder.query({
      query: ({ merchantId }) => `merchants/${merchantId}`,
      providesTags: (_, __, { merchantId }) => [{ type: 'merchant', merchantId }]
    })
  })
});

export const {
  useGetNotificationsQuery,
  useMarkNotificationMutation,
  useMarkAllNotificationsMutation,
  useGetDashboardQuery,
  useGetInvoicingStatsQuery,
  useGetInvoicesQuery,
  useGetCardTypeByMonthQuery,
  useGetCustomerLoyaltyQuery,
  useGetSpendStatsQuery,
  useGetReceiptsQuery,
  useGetAchReportQuery,
  useGetBatchReportQuery,
  useGetChargeBacksQuery,
  useGetChargeBackQuery,
  useGetNotesQuery,
  useGetApiKeysQuery,
  useUpdateApiKeysMutation,
  useGetStatementsReportQuery,
  useGetPaymentHistoryQuery,
  useGetRetrievalsQuery,
  useGetProfileQuery,
  useGetBlockedAccountsQuery,
  useGetInvoicingSettingsQuery,
  useGetMerchantAccessScopesQuery,
  useGetMerchantAccessStatusQuery,
  useGetMerchantUsersQuery,
  useAddMerchantUserMutation,
  useGetOAuthQuickbooksUserQuery,
  useGetOAuthSlackUserQuery,
  useGetSettingsQuery,
  useGetSuggestionsQuery,
  useGetSubMerchantsQuery,
  useGetTimeZonesQuery,
  useGetUsersQuery,
  useGetCustomerQuery,
  useGetCustomersQuery,
  useGetCapitalQuery,
  useCreateAccessTokenMutation,
  useGetTdsMerchantQuery,
  useCreateTdsMerchantMutation,
  useUpdateTdsMerchantMutation,
  useAuthGhostMerchantMutation,
  useGetMerchantQuery
} = officeApi;
